import { useEffect, useState } from "react";
import type { SMTPSettingsPublic } from "@agencylens/shared";
import { Button } from "@/components/ui/Button.js";
import { getSession } from "@/lib/session.js";
import {
  getSmtpSettings,
  putSmtpSettings,
  deleteSmtpSettings,
  testSmtpConnection,
} from "@/lib/smtp-api.js";

/**
 * Settings → Email Delivery panel.
 *
 * Tier-2+ buyers enter their own SMTP credentials here. The cron worker uses
 * them to send scheduled monthly reports from the buyer's verified sender
 * domain. The plaintext password leaves the browser exactly once (on PUT);
 * after that, the Worker stores an EncryptedBlob.
 *
 * Tier-1 buyers see a tier-gate message; the underlying worker rejects PUT
 * with 402 anyway.
 */
export function EmailDeliveryPanel({
  onError,
}: {
  onError: (msg: string | undefined) => void;
}) {
  const session = getSession();
  const tier = session?.payload.tier;
  const tierIsPaid = tier === "tier-2" || tier === "tier-3";

  const [current, setCurrent] = useState<SMTPSettingsPublic | null>(null);
  const [busy, setBusy] = useState<string | undefined>();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    host: "",
    port: 587,
    user: "",
    password: "",
    fromEmail: "",
    fromName: "",
    secure: false,
  });

  useEffect(() => {
    if (!session || !tierIsPaid) return;
    (async () => {
      try {
        setBusy("Loading email settings…");
        const r = await getSmtpSettings(session.jwt);
        setCurrent(r.smtp);
        if (r.smtp) {
          setForm({
            host: r.smtp.host,
            port: r.smtp.port,
            user: r.smtp.user,
            password: "",
            fromEmail: r.smtp.fromEmail,
            fromName: r.smtp.fromName ?? "",
            secure: r.smtp.secure ?? false,
          });
        }
      } catch (e) {
        const ee = e as Error & { error?: string };
        // Tier-too-low is rendered inline; other errors surface up.
        if (ee.error !== "tier_too_low") {
          onError(`Could not load SMTP settings: ${ee.error ?? ee.message}`);
        }
      } finally {
        setBusy(undefined);
      }
    })();
  }, [session?.jwt, tierIsPaid, onError]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!session) return;
    onError(undefined);
    setBusy("Saving SMTP settings…");
    try {
      const r = await putSmtpSettings(session.jwt, {
        host: form.host.trim(),
        port: form.port,
        user: form.user.trim(),
        password: form.password,
        fromEmail: form.fromEmail.trim(),
        ...(form.fromName.trim() ? { fromName: form.fromName.trim() } : {}),
        secure: form.secure,
      });
      setCurrent(r.smtp);
      setEditing(false);
      setForm((f) => ({ ...f, password: "" }));
    } catch (e) {
      const ee = e as Error & { error?: string; hint?: string };
      onError(`${ee.error ?? ee.message}${ee.hint ? ` — ${ee.hint}` : ""}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onTest() {
    if (!session) return;
    onError(undefined);
    setBusy("Sending test email…");
    try {
      const r = await testSmtpConnection(session.jwt);
      setCurrent(r.smtp);
      if (!r.ok) {
        onError(`Test send failed: ${r.error ?? "unknown error"}`);
      }
    } catch (e) {
      onError(`Test send failed: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onRemove() {
    if (!session) return;
    if (!window.confirm("Remove your SMTP settings? Scheduled reports will stop sending until you add credentials again.")) {
      return;
    }
    onError(undefined);
    setBusy("Removing…");
    try {
      await deleteSmtpSettings(session.jwt);
      setCurrent(null);
      setEditing(false);
      setForm({
        host: "",
        port: 587,
        user: "",
        password: "",
        fromEmail: "",
        fromName: "",
        secure: false,
      });
    } catch (e) {
      onError(`Delete failed: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="mb-2 text-lg font-medium">Email delivery (SMTP)</h2>
      <p className="mb-4 text-sm text-slate-600">
        Bring your own SMTP credentials so scheduled monthly reports go out from{" "}
        <em>your</em> verified sender domain. AgencyLens never sees the recipients'
        replies — we only deliver to your SMTP provider, which then sends to your
        client.
      </p>

      {!tierIsPaid && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Scheduled email delivery is a <strong>Tier 2+</strong> feature. Your
          current tier is <strong>{tier ?? "unknown"}</strong>. Upgrade in the
          AgencyLens AppSumo listing to unlock.
        </div>
      )}

      {tierIsPaid && current && !editing && (
        <div className="space-y-2">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                Configured
              </span>
              <span className="font-mono text-xs text-slate-600">
                {current.host}:{current.port}
              </span>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <dt className="text-slate-500">From</dt>
              <dd className="text-slate-800">
                {current.fromName ? `${current.fromName} <${current.fromEmail}>` : current.fromEmail}
              </dd>
              <dt className="text-slate-500">SMTP user</dt>
              <dd className="font-mono text-slate-800">{current.user}</dd>
              <dt className="text-slate-500">TLS</dt>
              <dd className="text-slate-800">
                {current.secure ? "Implicit (port 465)" : "STARTTLS (port 587 typical)"}
              </dd>
              {current.lastTestedAt && (
                <>
                  <dt className="text-slate-500">Last tested</dt>
                  <dd className="text-slate-800">
                    {new Date(current.lastTestedAt).toLocaleString()} ·{" "}
                    {current.lastTestResult === "ok" ? (
                      <span className="text-emerald-700">ok</span>
                    ) : (
                      <span className="text-red-700">{current.lastTestResult}</span>
                    )}
                  </dd>
                </>
              )}
            </dl>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={onTest} disabled={busy !== undefined} variant="secondary">
              Send test email
            </Button>
            <Button onClick={() => setEditing(true)} disabled={busy !== undefined} variant="secondary">
              Edit
            </Button>
            <Button onClick={onRemove} disabled={busy !== undefined} variant="ghost">
              Remove
            </Button>
            {busy && <span className="self-center text-sm text-slate-500">{busy}</span>}
          </div>
        </div>
      )}

      {tierIsPaid && (!current || editing) && (
        <form onSubmit={onSave} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">SMTP host</span>
              <input
                type="text"
                required
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                placeholder="smtp.example.com"
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Port</span>
              <input
                type="number"
                required
                min={1}
                max={65535}
                value={form.port}
                onChange={(e) => setForm({ ...form, port: Number(e.target.value) })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
              <span className="text-xs text-slate-500">587 (STARTTLS) or 465 (implicit TLS)</span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">SMTP username</span>
              <input
                type="text"
                required
                value={form.user}
                onChange={(e) => setForm({ ...form, user: e.target.value })}
                placeholder="user@yourdomain.com"
                autoComplete="username"
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">SMTP password</span>
              <input
                type="password"
                required={!current}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                autoComplete="new-password"
                placeholder={current ? "(unchanged — leave blank)" : ""}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
              <span className="text-xs text-slate-500">
                Stored encrypted; never displayed back.
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">From email</span>
              <input
                type="email"
                required
                value={form.fromEmail}
                onChange={(e) => setForm({ ...form, fromEmail: e.target.value })}
                placeholder="reports@yourdomain.com"
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">From name (optional)</span>
              <input
                type="text"
                value={form.fromName}
                onChange={(e) => setForm({ ...form, fromName: e.target.value })}
                placeholder="Your Agency"
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
            <label className="col-span-full flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.secure}
                onChange={(e) => setForm({ ...form, secure: e.target.checked })}
                disabled={busy !== undefined}
              />
              <span>Use implicit TLS (port 465). Otherwise STARTTLS is used on port 587.</span>
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={busy !== undefined}>
              {current ? "Save changes" : "Save SMTP settings"}
            </Button>
            {current && (
              <Button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setForm((f) => ({ ...f, password: "" }));
                }}
                variant="ghost"
                disabled={busy !== undefined}
              >
                Cancel
              </Button>
            )}
            {busy && <span className="self-center text-sm text-slate-500">{busy}</span>}
          </div>
        </form>
      )}
    </section>
  );
}
